// src/utils/seatingChartSuggestion.js
//
// "Best available" seat suggestion. Pure functions, no React.
//
// Goals, in order:
//   1. keep the party together: a contiguous block of free seats in ONE row (no booked seat, aisle or other gap
//      in between). If no block of N exists, take the biggest blocks that do (3 -> 2+1 -> 1+1+1), the following
//      blocks preferring to be near the first one
//   2. only seats the buyer's tickets can actually be used on (checked with the same matching the map uses,
//      so a mix like 1 Adult + 1 Child still works)
//   3. closest to the "front": the stage if the chart has a text element that looks like one, else the middle of
//      the chart; same category preferred over a block that mixes categories
//
// The stage detection is a heuristic on canvas text ("SCENA", "Stage", ...). If you later store an explicit stage
// point on the chart, pass it as `focal` and this fallback is never used.

import { assignTicketTypes } from "./seatingChartAssignment";

const ROW_BUCKET = 12; // px: with no rowLabel, seats whose y differs by less than this count as one row
const STAGE_TEXT = /scen|stage|podium|estrad/i;

const median = (arr) => {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
};

export const findFocalPoint = (chart) => {
  const stage = (chart.canvasElements ?? []).find((e) => e.type === "text" && STAGE_TEXT.test(e.text ?? ""));
  if (stage) return { x: stage.x + (stage.width ?? 0) / 2, y: stage.y };

  const xs = (chart.seats ?? []).map((s) => s.x);
  const ys = (chart.seats ?? []).map((s) => s.y);
  if (!xs.length) return { x: 0, y: 0 };
  return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
};

const buildRows = (seats) => {
  const rows = new Map();
  seats.forEach((s) => {
    const key = `${s.sectionId ?? "-"}|${s.rowLabel ?? `y${Math.round(s.y / ROW_BUCKET)}`}`;
    if (!rows.has(key)) rows.set(key, []);
    rows.get(key).push(s);
  });
  return [...rows.values()].map((r) => r.sort((a, b) => a.x - b.x));
};

const centerOf = (block) => ({
  x: block.reduce((a, s) => a + s.x, 0) / block.length,
  y: block.reduce((a, s) => a + s.y, 0) / block.length,
});
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * @param {object}   p
 * @param {object}   p.chart        { seats: [{id, x, y, sectionId, rowLabel, categoryId}], canvasElements }
 * @param {Function} p.isFree       (seat) => boolean: available, sellable, not already selected
 * @param {Array}    p.selected     [{seatId, categoryId}] already selected
 * @param {object}   p.budget       tickets per type (including the ones that still need a seat)
 * @param {Map}      p.eligibility  categoryId -> ticketTypeId[]
 * @param {number}   p.count        how many seats to suggest (= tickets without a seat)
 * @param {object}   [p.focal]      {x, y} point to sit close to
 * @param {Set}      [p.keep]       seat ids the buyer already holds from this suggestion run: blocks that contain them
 *                                  get a strong bonus (so a retry extends a partial block instead of splitting the party)
 * @param {number}   [p.variety]    how many "seat pitches" worse than the very best block a block may be and still be
 *                                  a candidate. The result is picked AT RANDOM among all candidates within that
 *                                  tolerance, so two buyers asking at the same moment mostly get different seats
 *                                  (0 = always the single best block, deterministic)
 * @param {Function} [p.random]     () => [0,1), injectable for tests
 * @returns {string[]} seat ids
 */
export const suggestSeats = ({
  chart, isFree, selected, budget, eligibility, count, focal, keep, variety = 2, random = Math.random,
}) => {
  if (!chart?.seats?.length || count <= 0) return [];

  const rows = buildRows(chart.seats);
  const deltas = [];
  rows.forEach((row) => {
    for (let i = 1; i < row.length; i += 1) {
      const d = row[i].x - row[i - 1].x;
      if (d > 0) deltas.push(d);
    }
  });
  const pitch = median(deltas) || 28;
  const maxGap = pitch * 1.6; // wider than this between neighbours = aisle / missing seat
  const focalPoint = focal ?? findFocalPoint(chart);

  const chosen = [];
  const chosenIds = new Set();
  const asSeat = (s) => ({ seatId: s.id, categoryId: s.categoryId });

  const findBest = (size) => {
    const candidates = [];
    rows.forEach((row) => {
      for (let i = 0; i + size <= row.length; i += 1) {
        const win = row.slice(i, i + size);
        let ok = true;
        for (let k = 0; k < size && ok; k += 1) {
          const s = win[k];
          if (chosenIds.has(s.id) || !isFree(s)) ok = false;
          else if (k > 0 && s.x - win[k - 1].x > maxGap) ok = false;
        }
        if (!ok) continue;

        const candidate = [...selected, ...chosen.map(asSeat), ...win.map(asSeat)];
        if (!assignTicketTypes(candidate, budget, eligibility).ok) continue;

        const c = centerOf(win);
        let score = dist(c, focalPoint);
        if (chosen.length) score += 2 * dist(c, centerOf(chosen)); // later blocks stay near the first
        score += new Set(win.map((s) => s.categoryId)).size * pitch; // same category preferred
        if (keep?.size) score -= win.filter((s) => keep.has(s.id)).length * pitch * 6; // extend what we already hold
        candidates.push({ score, win });
      }
    });
    if (!candidates.length) return null;
    const bestScore = Math.min(...candidates.map((c) => c.score));
    const pool = candidates.filter((c) => c.score <= bestScore + pitch * variety);
    return pool[Math.floor(random() * pool.length)].win;
  };

  let remaining = count;
  while (remaining > 0) {
    let block = null;
    for (let size = remaining; size >= 1 && !block; size -= 1) block = findBest(size);
    if (!block) break;
    block.forEach((s) => {
      chosen.push(s);
      chosenIds.add(s.id);
    });
    remaining -= block.length;
  }
  return chosen.map((s) => s.id);
};